import { apiTemplateFolders } from "@/api/forms/template-folder.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiFormTemplateFolderPayload,
  TemplateFolderModule,
} from "@/types/forms/form-template-folder.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const templateFoldersKeys = createQueryKeys("templateFolders", {
  all: (module: TemplateFolderModule) => ({
    queryKey: [module],
    queryFn: async ({ signal }) => {
      const result = await apiTemplateFolders.getAll(module, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useTemplateFolders = (module: TemplateFolderModule) =>
  useQuery(templateFoldersKeys.all(module));

export const useTemplateFolderSave = <T extends FieldValues>(
  module: TemplateFolderModule,
  control: Control<T>
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: ApiFormTemplateFolderPayload }) => {
      const result =
        id !== undefined
          ? await apiTemplateFolders.update(module, id, payload)
          : await apiTemplateFolders.create(module, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateFoldersKeys.all(module).queryKey });
    },
    onError: (error: Error) => onError(error, control),
  });
};

export const useTemplateFolderDelete = (module: TemplateFolderModule) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const result = await apiTemplateFolders.delete(module, id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: templateFoldersKeys.all(module).queryKey });
    },
    onError: (error: Error) => onError(error),
  });
};
